import type { JSX } from "react";

import { Progress, Text } from "@mantine/core";
import clsx from "clsx";

import { formatTime } from "@/shared-utils";

import type { PacingState, PacingVisualState } from "../../hooks/usePacingState";

import classes from "./PacingPopover.module.css";

const stateColors: Record<PacingVisualState, string> = {
    neutral: "gray",
    onTrack: "teal",
    approaching: "yellow",
    exceeded: "red",
};

function getStatusText(pacing: PacingState): string {
    if (pacing.state === "exceeded") {
        return `${formatTime(pacing.overageSeconds, false)} over target`;
    }
    if (pacing.state === "approaching") {
        return "Approaching target";
    }
    return "On track";
}

interface PacingPopoverContentProps {
    pacing: PacingState;
}

export function PacingPopoverContent({ pacing }: PacingPopoverContentProps): JSX.Element {
    const elapsedClass = pacing.state !== "neutral" ? classes[pacing.state] : undefined;

    return (
        <div className={classes.popoverContent} data-testid="pacing-popover">
            <div className={classes.badge}>Call Pacing</div>
            {pacing.displayCategory ? <div className={classes.category}>{pacing.displayCategory}</div> : null}
            <div className={classes.timeRow}>
                <span className={clsx(classes.elapsed, elapsedClass)}>{formatTime(pacing.elapsedSeconds, false)}</span>
                <span className={classes.target}>/ {formatTime(pacing.targetSeconds, false)} target</span>
            </div>
            <Progress size="xs" value={pacing.progressPercent} color={stateColors[pacing.state]} />
            <Text className={classes.statusText} c={stateColors[pacing.state]}>
                {getStatusText(pacing)}
            </Text>
            {pacing.salesEligible ? (
                <div className={classes.salesContext}>
                    Sales eligible (+{formatTime(pacing.salesEligibleBufferSeconds, false)})
                </div>
            ) : null}
        </div>
    );
}
